/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { n, groupVariantsByColor, getVariantColorHex } from "../theme1/utils/theme1Helpers";
import { formatNumber } from "utils/formatNumber";
import { getCardGalleryImages } from "../../utils/productMainImages";
import MainImageCarousel from "../components/MainImageCarousel";
import Swal from "sweetalert2";

export default function ProductCard({ product, theme, onOpenProduct, onAddToCart, compact = false }) {
  const [selectedColorHex, setSelectedColorHex] = useState(null);
  const [selectedSizeLabel, setSelectedSizeLabel] = useState(null);

  const colorGroups = useMemo(() => groupVariantsByColor(product?.variants), [product]);
  const uniqueColors = useMemo(() => Object.values(colorGroups), [colorGroups]);
  const displayColors = useMemo(() => uniqueColors.filter((c) => c.hasColor), [uniqueColors]);

  const hasStockInGroup = (group) => {
    if (!group?.variants?.length) return false;
    return group.variants.some((v) => {
      const vSizes = v.options || v.sizes || [];
      if (vSizes.length === 0) return n(v.stockQty) > 0;
      const hasSizeStock = vSizes.some((s) => n(s.stock || s.stockQty) > 0);
      return hasSizeStock || n(v.stockQty) > 0;
    });
  };

  useEffect(() => {
    if (uniqueColors.length > 0) {
      const firstColor = uniqueColors.find((c) => hasStockInGroup(c)) || uniqueColors[0];
      if (!selectedColorHex || !colorGroups[selectedColorHex]) {
        setSelectedColorHex(firstColor.hex);
      }
      const activeGroup = colorGroups[selectedColorHex] || firstColor;
      const allSizes = [];
      activeGroup.variants.forEach((v) => {
        const vSizes = v.options || v.sizes || [];
        vSizes.forEach((s) => allSizes.push({ label: s.label || s.size, stock: n(s.stock || s.stockQty) }));
      });
      const firstInStock = allSizes.find((s) => s.stock > 0) || allSizes[0];
      if (firstInStock && !selectedSizeLabel) {
        setSelectedSizeLabel(firstInStock.label);
      }
    }
  }, [product, uniqueColors, selectedColorHex, colorGroups, selectedSizeLabel]);

  const activeColorGroup = colorGroups[selectedColorHex] || uniqueColors[0];

  const availableSizes = useMemo(() => {
    if (!activeColorGroup) return [];
    const sizeMap = new Map();
    activeColorGroup.variants.forEach((v) => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach((s) => {
        const label = s.label || s.size;
        let stock = n(s.stock || s.stockQty);
        if (stock <= 0 && n(v.stockQty) > 0) stock = n(v.stockQty);
        if (!sizeMap.has(label)) {
          sizeMap.set(label, { label, stock, variant: v });
        } else {
          const existing = sizeMap.get(label);
          existing.stock += stock;
        }
      });
    });
    return Array.from(sizeMap.values());
  }, [activeColorGroup]);

  const activeSizeObj = useMemo(() => {
    return availableSizes.find((s) => s.label === selectedSizeLabel) || availableSizes[0];
  }, [availableSizes, selectedSizeLabel]);

  const currentVariant = activeSizeObj?.variant || activeColorGroup?.variants?.[0];
  const galleryImages = useMemo(
    () => getCardGalleryImages(product, currentVariant?.imageUrl),
    [product, currentVariant]
  );

  const priceDetails = useMemo(() => {
    if (!currentVariant) return { base: 0, discount: 0, final: 0, percentage: 0 };

    const basePrice = Number(currentVariant.price || 0);
    let discountAmount = Number(currentVariant.discount || 0);

    if (discountAmount === 0 && Number(product.discountValue) > 0) {
      if (Number(product.discountType) === 1) {
        discountAmount = (basePrice * Number(product.discountValue)) / 100;
      } else {
        discountAmount = Number(product.discountValue);
      }
    }

    const finalPrice = basePrice - discountAmount;
    const percentage = basePrice > 0 ? Math.round((discountAmount / basePrice) * 100) : 0;

    return {
      base: basePrice,
      discount: discountAmount,
      final: Math.max(0, finalPrice),
      percentage,
    };
  }, [currentVariant, product.discountValue, product.discountType]);

  const isOutOfStock = useMemo(() => {
    if (availableSizes.length === 0) return Number(currentVariant?.stockQty || 0) <= 0;
    return availableSizes.every((s) => s.stock <= 0);
  }, [availableSizes, currentVariant]);

  const isProductOutOfStock = useMemo(() => {
    if (!uniqueColors.length) return Number(currentVariant?.stockQty || 0) <= 0;
    return uniqueColors.every((c) => !hasStockInGroup(c));
  }, [uniqueColors, currentVariant]);

  const showOutOfStockAlert = () => {
    Swal.fire({
      icon: "info",
      title: "انتهى من المخزون",
      text: "هذا المنتج غير متوفر حالياً",
    });
  };

  const handleOpenDetails = () => {
    if (isProductOutOfStock) {
      showOutOfStockAlert();
      return;
    }
    onOpenProduct({ productId: product.id, variantId: currentVariant?.variantId });
  };

  const handleQuickAdd = (e) => {
    e.stopPropagation();
    if (isOutOfStock) {
      showOutOfStockAlert();
      return;
    }

    if (!currentVariant) {
      Swal.fire({ title: "تنبيه", text: "يرجى اختيار المنتج أولاً", icon: "warning" });
      return;
    }

    onAddToCart({
      ...product,
      itemId: product.id,
      variantId: currentVariant.variantId,
      price: priceDetails.final,
      color: getVariantColorHex(currentVariant),
      colorHex: getVariantColorHex(currentVariant),
      hex: getVariantColorHex(currentVariant),
      colorName: getVariantColorHex(currentVariant) || "",
      size: selectedSizeLabel || "",
      itemName: product.name,
      stockQty: activeSizeObj ? activeSizeObj.stock : Number(currentVariant.stockQty || 0),
      imageUrl: currentVariant?.imageUrl || product.mainImageUrl || product.mainImage,
    }, 1);
  };

  const handleColorSelect = (hex) => {
    setSelectedColorHex(hex);
    const group = colorGroups[hex];
    const allSizes = [];
    group?.variants?.forEach((v) => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach((s) => allSizes.push({ label: s.label || s.size, stock: n(s.stock || s.stockQty) }));
    });
    const firstInStock = allSizes.find((s) => s.stock > 0) || allSizes[0];
    setSelectedSizeLabel(firstInStock?.label || null);
  };

  return (
    <article
      className={`t3-card ${compact ? "t3-card-compact" : ""} ${isProductOutOfStock ? "is-oos" : ""}`}
      onClick={handleOpenDetails}
    >
      <div className="t3-card-media">
        <MainImageCarousel
          images={galleryImages}
          alt={product.name}
          variant="theme3"
          compact={compact}
        />
        {priceDetails.discount > 0 && (
          <span className="t3-badge-sale">خصم {priceDetails.percentage}%</span>
        )}
        {isProductOutOfStock && (
          <div className="t3-oos">نفذت الكمية</div>
        )}
        <button
          type="button"
          className="t3-card-add"
          disabled={isOutOfStock}
          onClick={handleQuickAdd}
          style={{ background: isOutOfStock ? "#c5ced8" : theme.primary }}
          aria-label="أضف للسلة"
        >
          {isOutOfStock ? "—" : "+"}
        </button>
      </div>

      <div className="t3-card-body">
        {product.category && (
          <span className="t3-card-cat" style={{ color: theme.primary }}>{product.category}</span>
        )}
        <h3>{product.name}</h3>

        {!compact && displayColors.length > 0 && (
          <div className="t3-card-colors">
            {displayColors.map((c) => (
              <button
                key={c.hex}
                type="button"
                onClick={(e) => { e.stopPropagation(); handleColorSelect(c.hex); }}
                className={selectedColorHex === c.hex ? "is-active" : ""}
                style={{ backgroundColor: c.hex }}
              />
            ))}
          </div>
        )}

        {!compact && availableSizes.length > 0 && (
          <div className="t3-card-sizes">
            {availableSizes.map((opt, idx) => (
              <button
                key={idx}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (opt.stock <= 0) {
                    showOutOfStockAlert();
                    return;
                  }
                  setSelectedSizeLabel(opt.label);
                }}
                className={selectedSizeLabel === opt.label ? "is-active" : ""}
                style={selectedSizeLabel === opt.label ? { background: theme.primary, borderColor: theme.primary } : {}}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        <div className="t3-card-price">
          <strong style={{ color: theme.primary }}>{formatNumber(priceDetails.final)} <small>د.ع</small></strong>
          {priceDetails.discount > 0 && (
            <s>{formatNumber(priceDetails.base)}</s>
          )}
        </div>
      </div>
    </article>
  );
}
